import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { useContentOverflow } from '../../../hooks'
import { getUserId, getUserListItemContentText } from '../../../utils/user'
import { Avatar, AVATAR_HEIGHT_32 } from '../../Avatar'
import { FloatingTooltip, FloatingTooltipContentText } from '../../Floating'
import Text from '../../Text'
import { UserListItemAdditionalInfo } from '../UserListItemAdditionalInfo'

import {
  QA_NAME_USER_LOGGED,
  QA_NAME_USER_NAME,
} from './UserListItemContent.constants'
import { toUserListItemContent } from './UserListItemContent.utils'

import * as styles from './UserListItemContent.module.css'

import UserIcon from '../../../icons/user.svg'

function UserListItemContent({
  AdditionalInfo = UserListItemAdditionalInfo,
  ContentAvatar = Avatar,
  Icon,
  avatarSize = AVATAR_HEIGHT_32,
  blocked,
  currentUser,
  isActive,
  isFocused,
  isHovered,
  showTooltip,
  textQaName = QA_NAME_USER_NAME,
  tooltipAdditionalText,
  tooltipProps,
  value,
}) {
  const { t } = useTranslation()

  const { overflow, setContentOverflowRef } = useContentOverflow()
  const {
    overflow: secondLineOverflow,
    setContentOverflowRef: setSecondLineOverflowRef,
  } = useContentOverflow()

  const isCurrentUser = value === getUserId(currentUser)

  const user = toUserListItemContent(value)

  const {
    avatar,
    avatarColor,
    email,
    fullname,
    isPersistent,
    isSystem,
    status,
  } = user

  const textClassName = classNames(styles.text, {
    [styles.blocked]: blocked,
    [styles.hover]: !blocked && (isFocused || isHovered || isActive),
  })

  const name = isSystem ? t(fullname) : getUserListItemContentText(user)

  return value && (!isPersistent || isSystem) ? (
    <>
      <ContentAvatar
        Icon={isSystem ? UserIcon : Icon}
        color={isSystem ? null : avatarColor}
        size={avatarSize}
        src={avatar}
        status={status}
        text={fullname}
      />
      <FloatingTooltip
        {...tooltipProps}
        content={
          <FloatingTooltipContentText
            additionalText={tooltipAdditionalText || user.email}
            text={name}
          />
        }
        disabled={!(showTooltip || overflow || secondLineOverflow)}
      >
        <div className={styles.info}>
          <Text
            className={textClassName}
            qaName={textQaName}
            ref={setContentOverflowRef}
          >
            {name}
            {isCurrentUser ? (
              <Text disabled qaName={QA_NAME_USER_LOGGED}>
                {` (${t('Select.CommonListItemIsLoggedUser')})`}
              </Text>
            ) : null}
          </Text>
          {AdditionalInfo ? (
            <AdditionalInfo
              blocked={blocked}
              email={email}
              emailRef={setSecondLineOverflowRef}
              isHovered={isHovered}
              status={status}
            />
          ) : null}
        </div>
      </FloatingTooltip>
    </>
  ) : (
    <FloatingTooltip content={t(fullname)} disabled={!showTooltip}>
      <Text className={textClassName} qaName={textQaName}>
        {t(fullname)}
      </Text>
    </FloatingTooltip>
  )
}

export default UserListItemContent
